import { Injectable } from "@angular/core";
import { map, Observable, of, shareReplay, switchMap } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "projects/ai_modernization/src/environments/environment";
import { IRolePrivilegesData } from "../../interfaces/administration/rolePrivilegesData";
import { LoggedUserService } from "../common/logged-user.service";
import { ILoggedUserData } from "../../interfaces/common/loggedUserData";
import { ConfigService } from "../common/config.service";
import { Role, Privilege, AccessType, maxAccess } from "../../models/authorization";
import { AccessTypeMapper } from "../../utils/access-type-mapper";
const apiUrl = environment.apiUrl + "/role-privileges";

interface MockRolePrivilegesFile {
    roles: Record<string, Array<{ permissionKey: string; access: string }>>;
}

interface IRolePrivilegesCache {
    date: Date;
    rolePrivileges: IRolePrivilegesData[];
}

@Injectable({
    providedIn: "root",
})
export class RolePrivilegesDataService {
    private cacheDuration = 5 * 60 * 1000; // 5 minutes
    private roleCache: IRolePrivilegesCache = {
        date: new Date(),
        rolePrivileges: [],
    };

    private mockPrivilegesFile$?: Observable<MockRolePrivilegesFile>;

    constructor(
        private http: HttpClient,
        private loggedUserService: LoggedUserService,
        private configService: ConfigService
    ) { }

    /**
     * Loads privileges from the mock JSON file and merges them across roles.
     * Used by UserContextService when authorization.useMockPrivileges is enabled.
     */
    public getMockPrivilegesForRoles(roles: Role[]): Observable<Privilege[]> {
        return this.getMockPrivilegesFile().pipe(
            map((file) => {
                const privilegesMap = new Map<string, AccessType>();

                for (const role of roles) {
                    const entries = file.roles?.[role] ?? [];
                    for (const entry of entries) {
                        const permissionKey = entry.permissionKey;
                        const access = AccessTypeMapper.fromLegacy(entry.access);

                        const existing = privilegesMap.get(permissionKey);
                        privilegesMap.set(
                            permissionKey,
                            existing ? maxAccess(existing, access) : access
                        );
                    }
                }

                return Array.from(privilegesMap.entries())
                    .map(([permissionKey, access]) => ({ permissionKey, access }))
                    .filter((p) => p.access !== AccessType.None);
            })
        );
    }

    private getMockPrivilegesFile(): Observable<MockRolePrivilegesFile> {
        if (!this.mockPrivilegesFile$) {
            const authConfig = this.configService.getAuthorizationConfig();
            const path = authConfig.mockPrivilegesPath || "assets/mock/role-privileges.json";
            this.mockPrivilegesFile$ = this.http
                .get<MockRolePrivilegesFile>(path)
                .pipe(shareReplay(1));
        }
        return this.mockPrivilegesFile$;
    }

    public getRolePrivileges(dbRoleName: string): Observable<IRolePrivilegesData[]> {
        return this.http.get<IRolePrivilegesData[]>(
            `${apiUrl}/${dbRoleName}`
        );
    }

    public updateRolePrivileges(rolePrivilegesData: IRolePrivilegesData[]) {
        return this.http.put(`${apiUrl}/update`, rolePrivilegesData);
    }

    public getAllRolePrivileges(): Observable<IRolePrivilegesData[]> {
        const cachedRolePrivileges = this.roleCache.rolePrivileges;
        const now = new Date();
        if (cachedRolePrivileges && cachedRolePrivileges.length && (now.getTime() - new Date(this.roleCache.date).getTime()) < this.cacheDuration) {
            return of(cachedRolePrivileges);
        } else {
            return this.loggedUserService.getLoggedUserData().pipe(
                switchMap((loggedUser: ILoggedUserData | null) => {
                    if (loggedUser) {
                        return this.getMergedPrivileges(loggedUser.email).pipe(
                            map(data => {
                                this.roleCache = {
                                    date: now,
                                    rolePrivileges: data,
                                };
                                return data;
                            })
                        );
                    } else {
                        console.error("Cannot fetch logged user data in add-agency form.");
                        return of([] as IRolePrivilegesData[]);
                    }
                })
            );
        }
    }

    public getSpecificRolePrivilege(pageName: string, subPageName: string): Observable<IRolePrivilegesData> {
        const cachedRolePrivileges = this.roleCache.rolePrivileges.find(x => x.pageCategory == pageName
            && x.pageSubCategory == subPageName);
        const now = new Date();
        if (cachedRolePrivileges && (now.getTime() - new Date(this.roleCache.date).getTime()) < this.cacheDuration) {
            return of(cachedRolePrivileges);
        } else {
            return this.loggedUserService.getLoggedUserData().pipe(
                switchMap((loggedUser: ILoggedUserData | null) => {
                    if (loggedUser) {
                        return this.getMergedPrivileges(loggedUser.email).pipe(
                            map(data => {
                                this.roleCache = {
                                    date: now,
                                    rolePrivileges: data,
                                };
                                let result = data.find(x => x.pageCategory == pageName && x.pageSubCategory == subPageName);
                                return result;
                            })
                        );
                    } else {
                        console.error("Cannot fetch logged user data in add-agency form.");
                        return of({} as IRolePrivilegesData);
                    }
                })
            );
        }
    }

    private getMergedPrivileges(userEmail: string): Observable<IRolePrivilegesData[]> {
        // const encodedUserEmail = encodeURIComponent(userEmail);
        // return this.http.get<IRolePrivilegesData[]>(
        //     `${apiUrl}/merged/${encodedUserEmail}`
        // );

        // Mock data
        const mockPrivileges: IRolePrivilegesData[] = [
            {
                rpid: 1,
                userRole: "Admin",
                pageCategory: "Administration",
                pageSubCategory: "Main",
                accessType: "Full",
                lastUpdatedBy: "System",
                lastUpdatedDate: new Date()
            },
            {
                rpid: 2,
                userRole: "Admin",
                pageCategory: "Administration",
                pageSubCategory: "Users",
                accessType: "Full",
                lastUpdatedBy: "System",
                lastUpdatedDate: new Date()
            },
            {
                rpid: 3,
                userRole: "Admin",
                pageCategory: "Home",
                pageSubCategory: "Dashboard",
                accessType: "Read",
                lastUpdatedBy: "System",
                lastUpdatedDate: new Date()
            }
        ];
        
        return of(mockPrivileges);
    }
}